import React, { useState, useRef, useEffect } from 'react';
import {
  LayoutList,
  FileText,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  DollarSign,
  Users,
  Trophy,
  Mars,
  Venus,
  Share2,
  MessageCircle,
  MapPin
} from 'lucide-react';
import { Tournament, Team, Club, Config, UserProfile, Player, RankingConfig } from '../../types';
import { ChampionBadge } from '../common/ChampionBadge';
import { SymmetricBracket } from './SymmetricBracket';
import { PublicTournamentLobby } from './PublicTournamentLobby';
import { RuleButton } from '../common/RuleButton';
import { PairAvatar } from '../common/PairAvatar';
import { FadeInImage } from '../common/FadeInImage';
import { ALL_CITIES } from '../../data/locations';
import { HelpCircle } from 'lucide-react';

interface PublicTournamentCardProps {
  key?: any;
  index: number;
  tournament: Tournament;
  config: Config;
  rankingConfig: RankingConfig;
  allTeams: Team[];
  players: Player[];
  clubs: Club[];
  sedesPublicas?: UserProfile[];
  onPlayerClick: (id: string | null) => void;
  onJoin: (id: string, partnerId: string) => void;
  onCancelJoin: (id: string) => void;
  onOpenChat: (organizerId: string) => void;
  handleShareCanvas: (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => void;
  hideShareButton?: boolean;
  hideLocation?: boolean;
  allUsers?: import('../../types').UserProfile[];
  isExpanded: boolean;
  onToggle: () => void;
}

const processedLogoCache = new Map<string, string>();

export function PublicTournamentCard({
  index,
  tournament,
  config,
  rankingConfig,
  allTeams,
  players,
  clubs,
  sedesPublicas = [],
  onPlayerClick,
  onJoin,
  onCancelJoin,
  onOpenChat,
  handleShareCanvas,
  hideShareButton,
  hideLocation,
  allUsers = [],
  isExpanded,
  onToggle,
}: PublicTournamentCardProps) {
  const isLobby = tournament.status === 'Lobby' || tournament.status === 'Registro' || tournament.status === 'Borrador' || !tournament.status;
  const isActiveStatus = tournament.status === 'En Juego' || tournament.status === 'Fase de Grupos' || tournament.status === 'Eliminatorias';
  const registeredCount = tournament.registeredTeamIds?.length || 0;
  const clubOrganizer = sedesPublicas.find(s => s.uid === tournament.organizerId) || allUsers?.find(u => u.uid === tournament.organizerId);
  const playerOrganizer = players.find(p => p.id === tournament.organizerId);
  
  const foundUser = allUsers?.find(u => u.uid === tournament.organizerId);
  const isSuperAdminOrganizer = clubOrganizer?.role === 'superadmin' || 
    tournament.organizerId === 'admin' || 
    tournament.organizerId === 'main_club' || 
    tournament.organizerId === 'agunavarro1989@gmail.com' || 
    foundUser?.role === 'superadmin' ||
    foundUser?.email === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.email === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.name?.toLowerCase().includes('agustin navarro');

  const organizerName = isSuperAdminOrganizer ? config.pageName : (playerOrganizer?.organizerProfile?.name || clubOrganizer?.name || playerOrganizer?.name || 'Torneo');
  const logoUrl = isSuperAdminOrganizer ? config.logoUrl : (playerOrganizer?.organizerProfile?.logoUrl || clubOrganizer?.photoUrl || config.logoUrl);
  const [processedLogo, setProcessedLogo] = useState<string | null>(() => {
    if (logoUrl && processedLogoCache.has(logoUrl)) {
      return processedLogoCache.get(logoUrl)!;
    }
    return null;
  });

  // Freeze whether it was in cache ON MOUNT so we can enforce fade-in if it wasn't.
  const [wasInCacheOnMount] = useState(() => logoUrl ? processedLogoCache.has(logoUrl) : false);

  useEffect(() => {
    let isActive = true;

    if (!logoUrl) {
      if (isActive) setProcessedLogo(null);
      return;
    }
    
    // If it's already processed, don't do it again
    if (processedLogo && processedLogo !== logoUrl && processedLogoCache.has(logoUrl)) {
      if (isActive) setProcessedLogo(processedLogoCache.get(logoUrl)!);
      return;
    }
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        // Chroma-keying out black (R,G,B < 35)
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] < 35 && data[i+1] < 35 && data[i+2] < 35) {
            data[i+3] = 0;
          }
        }
        const processed = canvas.toDataURL();
        processedLogoCache.set(logoUrl, processed);
        if (isActive) {
          setProcessedLogo(processed);
        }
      }
    };
    img.onerror = () => {
      processedLogoCache.set(logoUrl, logoUrl);
      if (isActive) {
        setProcessedLogo(logoUrl);
      }
    };

    return () => {
      isActive = false;
    };
  }, [logoUrl]);

  const getZoneTeamInfo = (t: any) => {
    if (t.teamId) {
      const found = allTeams.find((tm) => tm.id === t.teamId);
      if (found) {
        const p1 = players.find(p => p.id === found.player1Id);
        const p2 = players.find(p => p.id === found.player2Id);

        // Prioritize player names from the players list, fallback to team name parts
        const nameParts = found.name.includes(' / ') ? found.name.split(' / ') :
          found.name.includes('/') ? found.name.split('/') :
            found.name.includes(' & ') ? found.name.split(' & ') : [found.name];

        const p1Name = p1?.name || nameParts[0]?.trim() || 'Jugador 1';
        const p2Name = p2?.name || nameParts[1]?.trim() || (nameParts.length > 1 ? 'Jugador 2' : '');

        return {
          id: found.id,
          p1Id: found.player1Id,
          p2Id: found.player2Id,
          p1Photo: p1?.photoUrl,
          p2Photo: p2?.photoUrl,
          p1Name,
          p2Name,
          photoUrl: found.photoUrl
        };
      }
      return { id: null, p1Name: t.name, p2Name: '', photoUrl: null, p1Id: null, p2Id: null, p1Photo: null, p2Photo: null };
    }
    return { id: null, p1Name: t.name, p2Name: '', photoUrl: null, p1Id: null, p2Id: null, p1Photo: null, p2Photo: null };
  };

  const formatShortName = (fullName: string) => {
    if (!fullName) return '';
    const names = fullName.trim().split(/\s+/);
    if (names.length < 2) return fullName;
    const initial = names[0].charAt(0).toUpperCase();
    const surname = names[names.length - 1];
    return `${initial}. ${surname}`;
  };

  const champion = tournament.championId ? allTeams.find((t) => t.id === tournament.championId) : null;

  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      // Small timeout to ensure the tab switch and content rendering are complete
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const isEven = index % 2 === 0;

  const renderLocation = () => {
    const cityName = ALL_CITIES.find(c => c.id === tournament.locationCity)?.name || tournament.locationCity || 'Ciudad no especificada';

    if (tournament.locationMode === 'clubs' && tournament.locationClubIds && tournament.locationClubIds.length > 0) {
      const selectedClubs = sedesPublicas.filter(s => tournament.locationClubIds!.includes(s.uid));
      if (selectedClubs.length > 0) {
        return (
          <div className="flex flex-wrap gap-2">
            {selectedClubs.map(c => {
              const clubCityName = ALL_CITIES.find(city => city.id === c.city)?.name || c.city || 'Ciudad no especificada';
              return (
                <div key={c.uid} className="flex items-center gap-2.5 bg-black/40 rounded-xl p-2 pr-4 border border-white/5">
                  <div className="w-10 h-10 rounded-lg bg-black overflow-hidden shrink-0">
                    {c.photoUrl ? (
                      <img src={c.photoUrl} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center theme-primary-bg text-black">
                        <MapPin size={16} />
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-[7px] font-black uppercase tracking-widest text-white/50 mb-0.5">Dónde se juega</div>
                    <div className="text-xs font-bold text-white leading-tight">{c.name}</div>
                    <div className="text-[9px] opacity-60 leading-none mt-1">{clubCityName}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
    } else if (tournament.locationMode === 'tbd') {
      return (
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2.5 bg-black/40 rounded-xl p-2 pr-4 border border-white/5">
            <div className="w-10 h-10 rounded-lg bg-black overflow-hidden shrink-0">
              <div className="w-full h-full flex items-center justify-center theme-primary-bg text-black">
                <HelpCircle size={16} />
              </div>
            </div>
            <div className="text-left">
              <div className="text-[7px] font-black uppercase tracking-widest text-white/50 mb-0.5">Dónde se juega</div>
              <div className="text-xs font-bold text-white leading-tight">Club: A determinar</div>
              <div className="text-[9px] opacity-60 leading-none mt-1">{cityName}</div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      data-tournament-id={tournament.id}
      ref={cardRef}
      className={`group border rounded-2xl relative isolate mx-2 transition-all duration-300 ${!isExpanded ? 'hover:scale-[1.01]' : ''} ${isExpanded
        ? 'border-white/50 shadow-[0_4px_10px_rgba(0,0,0,0.35)] z-20'
        : 'border-white/5 hover:border-white/20 shadow-[0_4px_10px_rgba(0,0,0,0.35)]'}`}
    >
      {/* Glass Header — always visible with glass, even when expanded */}
      <div className={`bg-[#4aa6d6]/25 transition-colors duration-300 group-hover:bg-[#4aa6d6]/35 relative overflow-hidden ${isExpanded ? 'rounded-t-2xl pt-4 px-4 md:pt-5 md:px-5 pb-0' : 'p-4 md:p-5 rounded-2xl'}`}>
        {/* Status Tags */}
        <div className="absolute top-0 right-0 flex z-30 pointer-events-none">
          {isLobby && (
            <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-lime-500/20 text-lime-400 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-lime-500/30">
              <div className="w-1.5 h-1.5 rounded-full bg-lime-500/80 animate-pulse" />
              INSCRIPCIÓN
            </span>
          )}
          {isActiveStatus && (
            <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-white/10 text-white/90 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-white/20">
              <div className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
              {tournament.status?.toUpperCase()}
            </span>
          )}
        </div>

        {/* Tournament Banner - Enhanced Size */}
        <div
          className={`relative z-10 flex items-center justify-between cursor-pointer group gap-4 transition-all ${isExpanded ? 'pb-4' : ''}`}
          onClick={onToggle}
        >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex flex-col items-center justify-center gap-1.5 shrink-0 pr-4 md:pr-5 border-r border-white/10">
            <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Organiza:</span>
            
            {logoUrl && (logoUrl !== config.logoUrl || isSuperAdminOrganizer) ? (
              <div className="w-16 h-16 md:w-24 md:h-24 flex items-center justify-center">
                <FadeInImage 
                  src={processedLogo || undefined}
                  alt={organizerName}
                  className="w-full h-full object-contain"
                  forceFadeIn={logoUrl ? !wasInCacheOnMount : false}
                />
              </div>
            ) : (
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl flex items-center justify-center theme-primary-bg text-black shadow-lg">
                <Trophy size={24} />
              </div>
            )}
            
            <span className="text-[8px] md:text-[9px] font-black uppercase tracking-wider text-theme-primary truncate w-full text-center max-w-[80px] md:max-w-[100px]">
              {organizerName}
            </span>
          </div>

          <div className="flex flex-col min-w-0 gap-1.5">
            <h3 className="text-lg md:text-xl font-black theme-primary-text theme-font-secondary uppercase italic leading-none flex flex-col items-start gap-1.5 pt-2 md:pt-3">
              <span className="break-words w-full [text-wrap:balance]">{tournament.name}</span>
            </h3>

            {/* 2, 3, 4, 5. Combined Metadata Row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {/* 2. Category */}
              {tournament.category && (
                <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-white/30 text-white shrink-0">
                  {tournament.category.toLowerCase() === 'libre' ? 'CAT. LIBRE' : tournament.category}
                </span>
              )}

              {/* 3. Gender */}
              {tournament.gender && (
                <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest shrink-0 px-2 py-0.5 rounded border border-white/30 text-white">
                  {tournament.gender.toLowerCase() === 'masculino' && <Mars size={10} />}
                  {tournament.gender.toLowerCase() === 'femenino' && <Venus size={10} />}
                  {(!['masculino', 'femenino', 'sin_restriccion', 'sin restricción'].includes(tournament.gender.toLowerCase())) && (
                    <div className="flex -space-x-1 text-yellow-400">
                      <Mars size={10} />
                      <Venus size={10} />
                    </div>
                  )}
                  {tournament.gender.toLowerCase() === 'masculino' ? 'Caballeros' :
                    tournament.gender.toLowerCase() === 'femenino' ? 'Damas' : 
                    (tournament.gender.toLowerCase() === 'sin_restriccion' || tournament.gender.toLowerCase() === 'sin restricción') ? 'Género Libre' : 'Mixto'}
                </div>
              )}

              {/* 4. Registered Players (Cupos) */}
              <div className="flex items-center gap-1.5 opacity-70 shrink-0">
                <Users size={12} className="theme-primary-text" />
                <span className="text-[10px] font-bold uppercase tracking-tight">
                  {registeredCount}/{tournament.maxTeams || 16} Cupos
                </span>
              </div>

              {/* 5. Date (Shortened format) */}
              <div className="flex items-center gap-1.5 opacity-70 shrink-0">
                <CalendarDays size={12} className="theme-primary-text" />
                <span className="text-[10px] font-bold uppercase tracking-tight">
                  {tournament.startedAt
                    ? `Inicio: ${(() => {
                      const d = new Date(tournament.startedAt);
                      return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear().toString().slice(-2)}`;
                    })()}`
                    : tournament.startDate ? `Fecha (Estimada): ${(() => {
                      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                      if (dateRegex.test(tournament.startDate)) {
                        const [y, m, d] = tournament.startDate.split('-');
                        return `${parseInt(d)}-${parseInt(m)}-${y.slice(-2)}`;
                      }
                      return tournament.startDate;
                    })()}` : 'A definir'}
                </span>
              </div>
            </div>

            {/* 6. Champion Badge */}
            {champion && (
              <div className="transform scale-[0.80] origin-left -ml-1 mt-1">
                <ChampionBadge champion={champion} />
              </div>
            )}
          </div>
        </div>

          <div className="flex items-center gap-2 shrink-0">
            <div
              className={`flex items-center justify-center text-white/40 ml-2 transition-transform duration-300 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
            >
              <ChevronDown size={20} />
            </div>
          </div>
        </div>
      </div>{/* end glass header */}

      {/* Content — always in DOM, shown/hidden via max-height to avoid compositing glitch */}
      <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${isExpanded ? 'max-h-[20000px]' : 'max-h-0'}`}>
        <div className="bg-slate-900/50 px-4 md:px-5 pb-4 md:pb-5 pt-4 rounded-b-2xl border-t border-white/10">
          {isLobby ? (
            <div className="space-y-4">
              <div className="flex flex-wrap justify-center items-center gap-4 px-2 w-full">
                {!hideLocation && (
                  <div className="flex justify-center">
                    {renderLocation()}
                  </div>
                )}
                <div className="flex items-center gap-2.5 shrink-0">
                  {onOpenChat && tournament.organizerId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenChat(tournament.organizerId);
                      }}
                      className="flex items-center gap-2 rounded-lg py-1.5 px-3.5 bg-white/5 border border-white/10 text-[9px] font-black uppercase tracking-tighter text-white hover:bg-white/10 transition-colors"
                    >
                      <MessageCircle size={14} />
                      <div className="flex flex-col text-left leading-[1.1]">
                        <span>Consultar</span>
                        <span>Organizador</span>
                      </div>
                    </button>
                  )}
                  
                  <RuleButton
                    tournament={tournament}
                    className="!rounded-lg !py-1.5 !px-3 !bg-white/5 !border-white/10 !text-[8px] !font-black !uppercase !tracking-tighter hover:!bg-white/10 transition-colors"
                  />
                  
                  {!hideShareButton && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}${window.location.pathname}?t=${tournament.id}`;
                        const text = `¡Mira el torneo "${tournament.name}" en RPT! 🎾\n\nConsultá la grilla y anotate aquí:\n${url}`;
                        
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: tournament.name,
                              text: `¡Mira el torneo "${tournament.name}" en RPT! 🎾`,
                              url: url
                            });
                          } catch (err) {
                            console.error('Error sharing:', err);
                          }
                        } else {
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }
                      }}
                      className="text-white opacity-60 hover:opacity-100 hover:scale-110 active:scale-95 transition-all p-1"
                      title="Compartir torneo"
                    >
                      <Share2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              <PublicTournamentLobby
                tournament={tournament}
                allTeams={allTeams}
                players={players}
                allUsers={allUsers}
                onPlayerClick={onPlayerClick}
                onJoin={onJoin}
                onCancelJoin={onCancelJoin}
                onOpenChat={onOpenChat}
                config={config}
                rankingConfig={rankingConfig}
              />
            </div>
          ) : (
            <>
              {!hideLocation && (
                <div className="mb-6 px-2 flex justify-center">
                  {renderLocation()}
                </div>
              )}
              <div className="relative flex justify-center items-center mb-6 px-2">
                <RuleButton
                  tournament={tournament}
                  className="!rounded-lg !py-1.5 !px-3 !text-[9px] shadow-md hover:scale-105 transition-all"
                />
                {!hideShareButton && (
                  <button
                    onClick={async () => {
                      const url = `${window.location.origin}${window.location.pathname}?t=${tournament.id}`;
                      const text = `¡Mira el torneo "${tournament.name}" en RPT! 🎾\n\nConsultá la grilla, zonas y resultados aquí:\n${url}`;
                      
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: tournament.name,
                            text: `¡Mira el torneo "${tournament.name}" en RPT! 🎾`,
                            url: url
                          });
                        } catch (err) {
                          console.error('Error sharing:', err);
                        }
                      } else {
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                      }
                    }}
                    style={{ backgroundColor: '#a8d8a8', color: '#1a3a1a' }}
                    className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-lg hover:scale-105 active:scale-95 transition-all shadow-md"
                    title="Compartir torneo"
                  >
                    <Share2 size={14} />
                  </button>
                )}
              </div>


              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-10 relative z-10">
                {(tournament.zones || []).map((zone, zIdx) => {
                  const sortedTeams = [...zone.teams].sort((a, b) => {
                    if (b.pts !== a.pts) return b.pts - a.pts;
                    const diffB = (b.sf || 0) - (b.sc || 0);
                    const diffA = (a.sf || 0) - (a.sc || 0);
                    if (diffB !== diffA) return diffB - diffA;
                    return (b.sf || 0) - (a.sf || 0);
                  });

                  return (
                    <div key={zIdx} className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden shadow-2xl group/zone">
                      <div className="bg-gradient-to-r from-white/10 to-transparent px-4 py-3 flex justify-between items-center border-b border-white/5">
                        <h4 className="text-sm md:text-base font-black uppercase tracking-widest theme-font-secondary opacity-90">{zone.name}</h4>
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-white/20"></div>
                          <div className="w-1.5 h-1.5 rounded-full bg-white/10"></div>
                        </div>
                      </div>
                      <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-[10px] md:text-xs text-center border-separate border-spacing-0">
                          <thead className="bg-black/40 text-[8px] md:text-[9px] font-black uppercase tracking-tighter opacity-60">
                            <tr>
                              <th className="py-2 px-2 text-left">Equipo</th>
                              <th className="py-2 px-1 theme-primary-text">PTS</th>
                              <th className="py-2 px-1">PJ</th>
                              <th className="py-2 px-1">PG</th>
                              <th className="py-2 px-1">PP</th>
                              <th className="py-2 px-1">SF</th>
                              <th className="py-2 px-1">SC</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {sortedTeams.map((t, i) => {
                              const tInfo = getZoneTeamInfo(t);
                              const isQualifying = tournament.hasPlayoffs && tournament.qualifiersPerZone && (i < tournament.qualifiersPerZone);
                              const pp = (t.pj || 0) - (t.pg || 0);

                              return (
                                <tr key={i} className={`hover:bg-white/5 transition-all relative group/row ${isQualifying ? 'bg-theme-primary/[0.04]' : (i % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent')}`}>
                                  <td className="py-1 px-2 text-left font-bold flex items-center relative min-w-[110px] md:min-w-[140px]">
                                    {isQualifying && (
                                      <div className="absolute left-0 top-1 bottom-1 w-0.5 theme-primary-bg shadow-[0_0_10px_rgba(var(--glow-rgb),0.5)]"></div>
                                    )}
                                    <span className={`w-3 md:w-4 text-[8px] md:text-[9px] mr-1 ${isQualifying ? 'theme-primary-text font-black' : 'opacity-40'}`}>{i + 1}</span>
                                    <div className="flex items-center">
                                      {/* Professional Dual Photos using PairAvatar */}
                                      <PairAvatar
                                        p1Id={tInfo.p1Id}
                                        p2Id={tInfo.p2Id}
                                        p1Image={tInfo.p1Photo || tInfo.photoUrl}
                                        p2Image={tInfo.p2Photo}
                                        className="w-6 h-6 md:w-8 md:h-8 mr-1.5 md:mr-2"
                                      />
                                      <span className={`text-[9px] md:text-xs tracking-tight ${isQualifying ? 'theme-primary-text font-black' : 'opacity-80'}`}>
                                        {formatShortName(tInfo.p1Name)}
                                        {tInfo.p2Name && ` / ${formatShortName(tInfo.p2Name)}`}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-1 px-1 font-black theme-primary-text bg-white/[0.04]">{t.pts}</td>
                                  <td className="py-1 px-1 opacity-60 font-medium">{t.pj}</td>
                                  <td className="py-1 px-1 opacity-60 font-medium">{t.pg}</td>
                                  <td className="py-1 px-1 opacity-60 font-medium">{pp}</td>
                                  <td className="py-1 px-1 opacity-40 font-medium">{t.sf || 0}</td>
                                  <td className="py-1 px-1 opacity-40 font-medium">{t.sc || 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              <SymmetricBracket
                tournament={tournament}
                allTeams={allTeams}
                config={config}
                clubs={clubs}
                onPlayerClick={onPlayerClick}
                handleShareCanvas={handleShareCanvas}
              />
            </>
          )}
        </div>
      </div>{/* end content wrapper */}
    </div>
  );
}
